import { useCallback } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import scrollToTheTop from "@lib/scrollToTheTop";
import { PaymentSource, usePayment } from "@modules/payment";

import { ORDER_MESSAGE_ZONE } from "../constants";

// Wrapper arround usePayment hook, with order specific handling

type TOrderPaymentReturn = {
  isLoading: boolean;
  executeOrderPayment: (shipmentId: string, paymentMethod: string) => void;
};

const useOrderPayment = (): TOrderPaymentReturn => {
  const { t } = useTranslation();

  const setMessage = useMessage();

  const { isLoading, executePayment } = usePayment();

  const executeOrderPayment = useCallback(
    async (shipmentId: string, paymentMethod: string) => {
      const { isSuccess, overDue } = await executePayment(shipmentId!, paymentMethod!, PaymentSource.ORDER);
      if (overDue) {
        setMessage({
          message: t("order-message-payment_overdue"),
          props: {
            variant: "error",
            icon: <Icon icon="warning" size="1.45rem" />,
            canClose: true,
            padding: "0.45rem 0.75rem",
          },
          zone: ORDER_MESSAGE_ZONE,
        });
        scrollToTheTop();
        return;
      }
      if (!isSuccess) {
        setMessage({
          message: t("order-message-payment_failure"),
          props: {
            variant: "error",
            icon: <Icon icon="warning" size="1.45rem" />,
            canClose: true,
            padding: "0.45rem 0.75rem",
          },
          zone: ORDER_MESSAGE_ZONE,
        });
        scrollToTheTop();
      }
    },
    [executePayment, setMessage, t]
  );

  return { isLoading, executeOrderPayment };
};
export default useOrderPayment;
